/**
 * HUD. 픽시가 아니라 DOM으로 그린다. 글자가 또렷하고 손보기 쉽다.
 */
import { traitChip, traitMeritPct, traitNames } from '../sim/traits.ts';
import type { Trait } from '../sim/traits.ts';
import { MAX_SQUADS, ORDER_NAMES } from '../sim/commands.ts';
import { MATE_SKILL_BY_ID } from '../sim/mate-skills.ts';
import type { MateSkillDef } from '../sim/mate-skills.ts';
import { squadName } from '../ui/labels.ts';
import type { Order } from '../sim/commands.ts';
import { Team } from '../sim/units.ts';
import { Role } from '../sim/troops.ts';
import { SQUAD_COLORS } from './atlas.ts';
import type { World } from '../sim/world.ts';
import { meritScore, survivalPct } from '../game/score.ts';
import { foeMarkerText } from './foe-marker.ts';
import { RAID_BOUNTY, battleGold } from '../meta/army.ts';

export interface HudDeps {
  readonly deployed: number;
  /** 단신 토벌인가. 병사를 걸지 않는 판이라 아래 줄에 다른 것을 보여야 한다. */
  readonly raid?: boolean;
  /** 토벌의 현상금(고른 도적 무리의 것, `RAID_BANDS`). 없으면 가장 작은 무리의 값이다. */
  readonly bounty?: number;
  /** 병사 uid → 이름. 전사 알림이 이름을 부른다. 없으면 "병사"로 부른다. */
  readonly names?: ReadonlyMap<number, string>;
  /** 이 판의 전장 특성(`battleTraits`). 전과 가산을 같이 셈하고, 아래 줄에 이름을 적는다. */
  readonly traits?: readonly Trait[];
  /** 관문 이야기의 이름(`gateStoryName`). 있으면 칩에 특성 대신 이 이름을 적는다. */
  readonly gate?: string | null;
}

/** 전사 알림이 머무는 프레임 수(60fps에 약 4초)와 한 번에 보이는 줄 수. */
const DEATH_FRAMES = 240;
const DEATH_SHOWN = 4;

export class Hud {
  private readonly stats: HTMLElement;
  private readonly squads: HTMLElement;
  private readonly controls: HTMLElement;
  private readonly bottom: HTMLElement;
  /** 장수 체력 줄 칸(`bottom` 안). 매 틱 다시 쓴다. */
  private readonly bottomBody: HTMLElement;
  /**
   * 장수 체력 줄 오른쪽의 빈 자리. 마우스 화면에서는 함성 단추가 여기 앉는다(`BattleControls.dockRoar`). 체력 줄은 매 틱
   * `innerHTML`로 다시 쓰므로 단추를 그 안에 두면 지워진다 — 그래서 칸을 둘로 나눴다.
   */
  readonly roarDock: HTMLElement;
  private readonly toast: HTMLElement;
  /** 화면 밖 적을 가리키는 표시. 적이 하나라도 보이면 숨긴다. */
  private readonly marker: HTMLElement;
  private readonly deployed: number;
  private readonly raid: boolean;
  private readonly bounty: number;
  /** 전장 특성의 전과 가산(`traitMeritPct`)과 이름 줄. 정산(`applyBattle`)과 같은 가산을 봐야 전과가 맞는다. */
  private readonly bonusPct: number;
  private readonly traitLine: string;
  private toastTimer = 0;
  /** 쓰러진 병사의 알림. 아군 병사가 쓰러지면 "OO 쓰러짐"이 잠깐 뜬다. */
  private readonly deaths: HTMLElement;
  /**
   * 화면 낭독기용 한 줄. 새 전사 한 건만 담는다. 보이는 목록(최근 넷)을 통째로 알림 영역에
   * 두었더니 새 전사마다 앞의 이름까지 다시 읽혔다(리뷰가 짚었다).
   */
  private readonly deathsLive: HTMLElement;
  private readonly names: ReadonlyMap<number, string>;
  /** 이미 알린 유닛(전장 색인). 한 번만 알린다. */
  private readonly announced = new Set<number>();
  private readonly feed: { text: string; until: number }[] = [];
  private frame = 0;

  constructor(root: HTMLElement, deps: HudDeps) {
    this.deployed = deps.deployed;
    this.raid = deps.raid === true;
    this.bounty = deps.bounty ?? RAID_BOUNTY;
    this.names = deps.names ?? new Map();
    this.bonusPct = traitMeritPct(deps.traits);
    // 칩은 한 묶음에 넣는다. `.merit`가 칸을 양끝으로 벌리므로 칩을 낱낱이 두면 칩끼리 흩어진다(리뷰가 짚었다).
    // 장수 체력 막대 안 오른쪽에 둔다. 처음에는 전과 줄에 칩을 넣었더니 360 폭에서 그 줄이 두 줄로 불어나 오른쪽 위
    // 부대 목록을 덮었다(QA가 짚었다). 전과 가산(+N%)도 여기 함께 적는다 — 전과 옆에 붙이면 같은 까닭으로 줄이 넘친다.
    // 관문은 특성 대신 이야기 이름을 적는다. 특성 셋을 늘어놓았더니(왕의 친위대) 모든 폭에서 체력 숫자를 덮었다(QA가
    // 짚었다). 특성 목록은 `title`로 남긴다.
    // 여느 판도 특성이 셋이면 줄인다(`traitChip`) — 요철이 붙으며 관문 밖에서도 셋이 나온다.
    const names = traitNames(deps.traits);
    const label = deps.gate ?? traitChip(deps.traits);
    this.traitLine =
      names.length > 0
        ? `<span class="bar-traits" title="${names.join(' · ')}">${label} +${this.bonusPct}%</span>`
        : '';

    this.stats = document.createElement('div');
    this.stats.className = 'hud hud-stats';

    this.squads = document.createElement('div');
    this.squads.className = 'hud hud-squads';

    this.controls = document.createElement('div');
    this.controls.className = 'hud hud-controls';
    this.controls.innerHTML = [
      '<b>조작</b>',
      '<span><kbd>WASD</kbd> 몰기 — 장수, 또는 고른 부대의 부대장 (떼면 스스로 싸운다)</span>',
      '<span>아래 버튼으로도 부대를 고르고 명령한다</span>',
      '<span><kbd>좌클릭</kbd> / <kbd>Space</kbd> 휩쓸기</span>',
      // 기술은 출정 전에 고른다(능력치 화면, Todo10) — 함성·방진·일제사격. 설명은 단추의 제목에.
      '<span><kbd>R</kbd> 장수 기술 — 함성·방진·일제사격 중 들고 나온 것</span>',
      '<span><kbd>1</kbd>~<kbd>4</kbd> 부대 선택 — 고른 부대장을 몬다 · <kbd>0</kbd> 전 부대(장수)</span>',
      '<span><kbd>우클릭</kbd> 한 점 · <kbd>Q</kbd> 지우기</span>',
      '<span><kbd>Z</kbd> 대기 <kbd>X</kbd> 대형 <kbd>C</kbd> 공격 <kbd>V</kbd> 후퇴</span>',
      '<span><kbd>F8</kbd> 결정론 검증</span>',
    ].join('');

    this.bottom = document.createElement('div');
    this.bottom.className = 'hud hud-bottom';
    this.bottomBody = document.createElement('div');
    this.bottomBody.className = 'hud-bottom-body';
    this.roarDock = document.createElement('div');
    this.roarDock.className = 'hud-roar-dock';
    this.bottom.append(this.bottomBody, this.roarDock);

    this.toast = document.createElement('div');
    this.toast.className = 'hud hud-toast';
    this.toast.style.display = 'none';

    this.marker = document.createElement('div');
    // hud가 있어야 absolute다. 빼먹었더니 흐름에 놓여 체력 줄 뒤에 숨었다.
    this.marker.className = 'hud hud-marker';
    this.marker.style.display = 'none';

    this.deaths = document.createElement('div');
    this.deaths.className = 'hud hud-deaths';
    this.deaths.setAttribute('aria-hidden', 'true');
    this.deaths.style.display = 'none';
    this.deathsLive = document.createElement('div');
    this.deathsLive.className = 'sr-only';
    this.deathsLive.setAttribute('role', 'status');
    this.deathsLive.setAttribute('aria-live', 'polite');

    root.append(this.stats, this.squads, this.controls, this.bottom, this.toast, this.marker, this.deaths, this.deathsLive);
  }

  /**
   * 화면 밖 적의 방향. 화면 안에 적이 하나도 없거나, 있어도 화면 밖에 충분히 많을 때 띄운다(`foeMarkerText`).
   *
   * 화면이 작은 모바일에서는 적이 어디 있는지 모른 채 시작한다. 화면 밖 적 가운데 화면에 가장 가까운 적 쪽
   * 가장자리에 화살표를 붙이고 수를 적는다(하나도 안 보이면 남은 적 전부, 일부가 보이면 화면 밖의 수를 "+N"으로).
   * 살아 있는 적만 센다 — 빠져나간 적은 어디에도 없다.
   */
  pointToEnemies(
    world: World,
    view: { w: number; h: number; toScreen: (x: number, y: number, out: { x: number; y: number }) => void },
  ): void {
    // 모두 화면 좌표로 잰다. 가로 화면에서는 카메라가 90도 돌아 있어, 월드 사각형으로 재면 보이는 곳과
    // 어긋났다(`Renderer.rotated`).
    const W = view.w;
    const H = view.h;
    // HUD가 덮는 위아래 띠는 보이는 것이 아니다. 거기 선 적 때문에 표시가 꺼지면
    // 화면에 적이 없는데 표시도 없다. 띠의 높이는 실제 요소에서 잰다 — 체력 줄은
    // 좁은 화면에서 위, 넓은 화면에서 아래에 있고, 150px로 어림했더니 표시가
    // 장수 머리 위에 떴다.
    const { topInset, bottomInset } = this.hudInsets();
    const x0 = 60;
    const x1 = W - 60;
    const y0 = topInset;
    const y1 = H - bottomInset;
    const bcx = (x0 + x1) / 2;
    const bcy = (y0 + y1) / 2;
    const at = this.at;
    let seen = 0;
    let off = 0;
    let best = -1;
    let bestD = Infinity;
    let bx = 0;
    let by = 0;
    // 끝까지 센다. 처음에는 화면 안의 적을 하나 보면 멈췄다 — 그때는 "하나도 없을 때만" 띄웠다.
    for (let i = 0; i < world.count; i++) {
      if (world.alive[i] === 0 || world.team[i] !== Team.Enemy) continue;
      view.toScreen(world.x[i] / 256, world.y[i] / 256, at);
      if (at.x >= 0 && at.x <= W && at.y >= topInset && at.y <= H - bottomInset) {
        seen++;
        continue;
      }
      off++;
      const d = (at.x - bcx) * (at.x - bcx) + (at.y - bcy) * (at.y - bcy);
      if (d < bestD) {
        bestD = d;
        best = i;
        bx = at.x;
        by = at.y;
      }
    }
    const text = foeMarkerText(seen, off, this.markerPlus);
    this.markerPlus = text !== null && seen > 0;
    if (text === null || best < 0) {
      this.marker.style.display = 'none';
      return;
    }
    // HUD 띠를 뺀 사각형(화면 픽셀)의 가운데에서 적 쪽으로 나가 그 테두리에 닿는 점.
    // 처음에는 사방 44px로 두었더니 위쪽 표시가 체력 줄 뒤에 숨어 보이지 않았다.
    const dx = bx - bcx;
    const dy = by - bcy;
    const k = Math.min(
      ((x1 - x0) / 2) / Math.max(Math.abs(dx), 1e-6),
      ((y1 - y0) / 2) / Math.max(Math.abs(dy), 1e-6),
    );
    let sx = bcx + dx * k;
    const sy = this.dodge(sx, bcy + dy * k, y0, y1);
    // 위아래로 비켜도 부대 줄이나 조이스틱 위라면(둘 사이에 틈이 없다) 그 둘의 왼쪽으로 옮긴다. 폰을 눕힌 화면에서
    // 일부만 보이는 적의 표지("+N")가 오른쪽 가장자리로 가 부대 줄의 글자를 가렸다(QA).
    const free = this.leftOfBlockers(sx, sy);
    if (free !== null) sx = Math.max(x0, free);
    const deg = (Math.atan2(dy, dx) * 180) / Math.PI;
    this.marker.style.display = 'flex';
    this.marker.style.left = `${Math.round(sx)}px`;
    this.marker.style.top = `${Math.round(sy)}px`;
    this.marker.innerHTML = `<i style="transform:rotate(${Math.round(deg)}deg)"></i><span>${text}</span>`;
  }

  /**
   * 표시가 오른쪽 부대 줄 위에 앉으면 위아래로 비켜 준다(조이스틱은 이제 손가락 밑에 떠서 피하지 않는다). 짧은 가로 화면(폰을 눕힘)에서는 적이
   * 오른쪽에 있어 표시가 늘 오른쪽 가장자리로 가는데, 거기 위에 부대 줄, 아래에 조이스틱이 있어 둘을
   * 가렸다(QA가 짚었다). 가까운 쪽 바깥으로 옮기되 HUD 띠(`y0`~`y1`) 안에 둔다.
   */
  private dodge(sx: number, sy: number, y0: number, y1: number): number {
    const root = this.marker.parentElement;
    if (!root) return sy;
    const base = root.getBoundingClientRect();
    const pad = 22;
    let y = sy;
    // 조이스틱은 이제 손가락 밑에 뜬다(정해진 자리가 없다) — 피할 것에서 뺐다. 쥘 때마다 표지가 비켜 다니면 어지럽다.
    for (const el of [this.squads]) {
      if (!el || el.offsetParent === null) continue;
      const r = el.getBoundingClientRect();
      const left = r.left - base.left - pad;
      const right = r.right - base.left + pad;
      const top = r.top - base.top - pad;
      const bottom = r.bottom - base.top + pad;
      if (sx < left || sx > right || y < top || y > bottom) continue;
      const up = top;
      const down = bottom;
      y = y - up < down - y && up >= y0 ? up : down <= y1 ? down : up;
    }
    return Math.min(Math.max(y, y0), y1);
  }

  /**
   * 표지가 부대 줄·조이스틱·함성 단추와 겹치면 그것들의 왼쪽 끝(여유를 뺀 x), 안 겹치면 null. 함성 단추는 처음에 빠져
   * 있었다 — 부대 줄과 조이스틱만 피해 옮긴 자리가 함성 단추 뒤였다(재QA: 폰 가로 깊이 1에서 6틱).
   */
  private leftOfBlockers(sx: number, sy: number): number | null {
    const root = this.marker.parentElement;
    if (!root) return null;
    const base = root.getBoundingClientRect();
    // 표지는 가운데가 기준이다. 옆으로는 제 폭의 절반(44px 남짓), 위아래로는 높이의 절반 남짓을 여유로 본다 — 옆도 22로
    // 재면 가운데는 비켜 있어도 표지의 끝이 닿았다.
    const padX = 44;
    const padY = 22;
    let left = Infinity;
    let hit = false;
    for (const el of [this.squads, root.querySelector<HTMLElement>('.hud-roar')]) {
      if (!el || el.offsetParent === null) continue;
      const r = el.getBoundingClientRect();
      // 빈 그릇(함성 단추가 없는 판)은 자리를 차지하지 않는다.
      if (r.width === 0 || r.height === 0) continue;
      const l = r.left - base.left - padX;
      if (sx >= l && sx <= r.right - base.left + padX && sy >= r.top - base.top - padY && sy <= r.bottom - base.top + padY) hit = true;
      left = Math.min(left, l);
    }
    // 하나에만 걸려도 모두의 왼쪽으로 뺀다 — 한쪽만 피하면 옮긴 자리가 다른 쪽 위다(셋은 오른쪽에 모여 있다).
    return hit && left !== Infinity ? left - 8 : null;
  }

  /** 지난 프레임에 "+N" 표지가 떠 있었나(`foeMarkerText`의 문턱에 이력을 준다). */
  private markerPlus = false;

  /** `pointToEnemies`의 투영 그릇. */
  private readonly at = { x: 0, y: 0 };

  /** 체력 줄과 명령 버튼 줄이 덮는 위아래 높이(화면 픽셀). */
  private hudInsets(): { topInset: number; bottomInset: number } {
    const root = this.marker.parentElement;
    if (!root) return { topInset: 24, bottomInset: 80 };
    const rootRect = root.getBoundingClientRect();
    const hb = this.bottom.getBoundingClientRect();
    // 명령 버튼 줄은 늘 아래에 있다(`BattleControls`). 높이를 여기서 모르므로 어림한다.
    let topInset = 24;
    let bottomInset = 80;
    if (hb.top - rootRect.top < rootRect.height / 2) topInset = hb.bottom - rootRect.top + 16;
    else bottomInset = Math.max(bottomInset, rootRect.bottom - hb.top + 16);
    return { topInset, bottomInset };
  }

  update(world: World, fps: number, simMs: number): void {
    const ours = world.aliveCount(Team.Player);
    const theirs = world.aliveCount(Team.Enemy);
    // 빠져나간 병사도 산 것이다. 전과 표시가 정산(`applyBattle`)과 같은 값을 봐야 한다.
    const survivors = world.soldiersSurviving(Team.Player);
    const exited = world.stats.exited[Team.Player];
    const killScore = world.stats.killScore[Team.Player];
    const merit = meritScore({
      killScore,
      deployed: this.deployed,
      survivors,
      leaderAlive: world.leaderAlive(),
      depth: 1,
      bonusPct: this.bonusPct,
    });

    this.stats.innerHTML = [
      `<span class="big">${fps} FPS</span>`,
      `<span>시뮬 ${simMs.toFixed(2)}ms · 틱 ${world.tick}</span>`,
      `<span class="ours">아군 ${ours}</span> <span class="theirs">적군 ${theirs}</span>`,
    ].join('');

    this.squads.innerHTML = this.renderSquads(world);

    const leader = world.leaderIdx;
    const hp = leader >= 0 ? Math.max(0, world.hp[leader]) : 0;
    const maxHp = leader >= 0 ? world.maxHp[leader] : 1;
    const pct = Math.round((hp / maxHp) * 100);

    // 토벌은 병사를 한 명도 걸지 않으므로 생환도 전과도 없다. 그 자리에
    // "생환 0/0 · 배율 25% · 전과 0"을 띄우면 뭔가 잘못된 판처럼 보인다.
    // 혼자 나선 사람이 보고 싶은 것은 지금까지 번 돈과 두령이 살아 있는지다.
    const right = this.raid
      ? [
          `<span>노획 <b>${battleGold(killScore)}</b>냥</span>`,
          world.chiefAlive()
            ? `<span class="dim">두령을 베면 ${this.bounty.toLocaleString()}냥</span>`
            : `<span class="merit-total">두령 <b>+${this.bounty.toLocaleString()}</b>냥</span>`,
        ]
      : [
          `<span>생환 ${survivors} / ${this.deployed}${exited > 0 ? ` (이탈 ${exited})` : ''} · 배율 ${survivalPct(this.deployed, survivors)}%</span>`,
          `<span class="merit-total">전과 <b>${merit}</b></span>`,
        ];

    this.bottomBody.innerHTML = [
      `<div class="bar"><div class="bar-fill" style="width:${pct}%"></div>`,
      `<span class="bar-label">장수 ${hp} / ${maxHp}</span>${this.traitLine}</div>`,
      `<div class="merit">`,
      `<span>처치한 적의 격 <b>${killScore}</b></span>`,
      ...right,
      `</div>`,
    ].join('');

    if (this.toastTimer > 0) {
      this.toastTimer--;
      if (this.toastTimer === 0) this.toast.style.display = 'none';
    }
    this.updateDeaths(world);
  }

  /**
   * 아군 병사의 전사 알림. 병사를 작게 가며 한 명 한 명이 이름을 가진 사람이 됐다 — 숫자가
   * 줄어드는 것만 보이면 누가 쓰러졌는지 모른 채 싸운다.
   *
   * 빠져나간 병사는 알리지 않는다(`exited`). 산 사람이다.
   *
   * **"전사"가 아니라 "쓰러짐"으로 적는다.** 쓰러진 병사가 죽었는지 다쳤는지는 판이 끝나고 정산할 때 갈린다(부상
   * 30%) — 전장에서 "OO 전사"로 적었더니 다쳐서 돌아온 병사가 죽었다고 나왔다(플레이한 사람, 2026-10-07). 전사인지
   * 부상인지는 결과 화면이 알린다.
   */
  private updateDeaths(world: World): void {
    this.frame++;
    let changed = false;
    const fresh: string[] = [];
    for (let i = 0; i < world.count; i++) {
      if (world.alive[i] === 1 || world.exited[i] === 1) continue;
      if (world.team[i] !== Team.Player || world.role[i] !== Role.Soldier) continue;
      if (this.announced.has(i)) continue;
      this.announced.add(i);
      const name = this.names.get(world.uid[i]) ?? '병사';
      this.feed.push({ text: `${name} 쓰러짐`, until: this.frame + DEATH_FRAMES });
      fresh.push(`${name} 쓰러짐`);
      changed = true;
    }
    // 낭독은 이번 프레임에 새로 쓰러진 사람만. 한 프레임에 여럿이면 한 줄로 묶는다.
    if (fresh.length > 0) this.deathsLive.textContent = fresh.join(', ');
    while (this.feed.length > 0 && this.feed[0].until <= this.frame) {
      this.feed.shift();
      changed = true;
    }
    if (!changed) return;
    if (this.feed.length === 0) {
      this.deaths.style.display = 'none';
      this.deaths.textContent = '';
      return;
    }
    this.deaths.style.display = 'flex';
    this.deaths.innerHTML = this.feed
      .slice(-DEATH_SHOWN)
      .map((f) => `<span>${escapeHtml(f.text)}</span>`)
      .join('');
  }

  /** 분대 현황. 선택된 분대, 지휘관 생사, 군기 유무가 한눈에 보여야 한다. */
  private renderSquads(world: World): string {
    const members = new Int32Array(MAX_SQUADS);
    for (let i = 0; i < world.count; i++) {
      if (world.alive[i] === 0 || world.team[i] !== Team.Player) continue;
      if (world.role[i] !== Role.Soldier) continue;
      members[world.squad[i]]++;
    }

    const rows: string[] = ['<b>부대</b>'];
    for (let sq = 0; sq < MAX_SQUADS; sq++) {
      if (world.sqUsed[sq] === 0) continue;
      const selected = (world.selectedMask & (1 << sq)) !== 0;
      const hasCmdr = world.captainActive(sq);
      const name = squadName(sq);
      const order = ORDER_NAMES[world.sqOrder[sq] as Order];
      // 동료 고유 기술이 걸려 있으면 그 이름을 단다(Todo10) — 단추가 없는 기술이라 여기서만 보인다.
      const skill = world.mateActive(sq) ? mateSkillOf(world, sq) : undefined;
      const banner =
        (world.sqPathLen[sq] > 0 ? '<span class="flag">경로</span>' : '') + (skill ? `<span class="flag">${skill.name}</span>` : '');
      rows.push(
        `<span class="squad${selected ? ' on' : ''}" style="--sq:${SQUAD_COLORS[sq]}">` +
          `<i class="pip"></i>` +
          `<b>${sq + 1}</b> ` +
          // 지휘관이 쓰러지면 이름에 줄을 긋는다. 보정이 사라졌다는 뜻이다.
          `<em class="${hasCmdr ? '' : 'down'}">${name}</em> ` +
          // 동료 혼자 선 분대(단신 출전)는 0명으로 적으면 전멸한 분대와 같아 보인다.
          `<span class="dim">${order} · ${members[sq] === 0 && sq > 0 && hasCmdr ? '단신' : `${members[sq]}명`}</span> ${banner}</span>`,
      );
    }
    if (world.companionsDowned > 0) {
      rows.push(`<span class="dim">쓰러진 부하장수 ${world.companionsDowned}</span>`);
    }
    return rows.join('');
  }

  /**
   * 알림을 띄운다. `tone`: 우리에게 좋은 일(`good` — 적장을 벴다, 부하장수의 기술)·나쁜 일(`bad` — 장수가 쓰러졌다, 분대
   * 전멸)·그 밖(`info`). 플레이한 사람이 청했다(2026-10-07): "긍정적인 알림과 부정적인 알림을 색으로도 구분할 수 있게".
   * 그 전에는 모두 금빛이라 "부장 김흠순이 쓰러졌다"가 우리 사람의 일인지 한눈에 갈리지 않았다. 색만으로 가르지 않는다
   * — 글도 "적 부장"으로 적고(`fallTitle`) 머리에 표지를 단다(▲ 좋은 일, ▼ 나쁜 일: CSS).
   */
  say(html: string, frames = 300, tone: ToastTone = 'info'): void {
    this.toast.innerHTML = html;
    this.toast.dataset.tone = tone;
    this.toast.style.display = 'block';
    this.toastTimer = frames;
  }
}

/** 알림의 결(`Hud.say`). */
export type ToastTone = 'good' | 'bad' | 'info';

/** 그 분대에 걸린 동료 고유 기술(`World.mateWho`의 동료 id로 찾는다). */
export function mateSkillOf(world: World, sq: number): MateSkillDef | undefined {
  const who = world.mateWho[sq];
  return who >= 0 ? MATE_SKILL_BY_ID.get(world.specs[world.specIdx[who]]?.troop ?? '') : undefined;
}

/** 이름은 계정에서 온다. 손으로 고친 저장에 꺾쇠가 들어 있어도 HUD에 태그로 들어가지 않게. */
function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);
}
